import React, { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { API_BASE_URL } from "../utils/constant";
import { FiUpload, FiFileText, FiX, FiLoader, FiCheckCircle, FiAlertTriangle, FiArrowLeft } from "react-icons/fi";
import { motion } from "framer-motion";

const ApplyPage = () => {
  const location = useLocation();
  const job = location.state?.job;

  const [formData, setFormData] = useState({
    job: job?.id || "",
    name: "",
    email: "",
    phone: "",
    resume: null,
    cover_letter: "",
    dob: "",
    gender: "",
    address: "",
    country: "India", // Default value
    state: "",
    city: "",
    zip_code: "",
    skill: [],
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    if (type === "file") {
      setFormData({ ...formData, [name]: files[0] });
    } else if (type === "checkbox") {
      setFormData(prevState => ({
        ...prevState,
        skill: checked
          ? [...prevState.skill, value]
          : prevState.skill.filter(item => item !== value),
      }));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    const formDataToSend = new FormData();
    // Append all form data
    Object.keys(formData).forEach(key => {
        if (key === 'skill') {
            formData.skill.forEach(skillItem => formDataToSend.append('skill[]', skillItem));
        } else if (formData[key]) {
            formDataToSend.append(key, formData[key]);
        }
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/job-apply`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formDataToSend,
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(data.message || "Your application has been submitted successfully!");
        // Reset form or navigate away
      } else {
        setError(data.message || "An error occurred. Please check your submission.");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!job) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 text-center px-4">
            <h2 className="text-2xl font-bold text-slate-800">No Job Selected</h2>
            <p className="text-slate-600 mt-2">Please select a job opening before applying.</p>
            <Link to="/career" className="inline-flex items-center mt-6 bg-blue-600 text-white font-semibold py-2 px-5 rounded-lg shadow-sm hover:bg-blue-700 transition-colors">
                <FiArrowLeft className="mr-2" />
                Back to Careers
            </Link>
        </div>
    );
  }

  return (
    <main className="bg-slate-50 min-h-screen py-16 sm:py-24">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
            className="bg-white shadow-xl rounded-2xl border border-slate-200/80"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
        >
          {/* --- Form Header --- */}
          <div className="p-6 sm:p-8 border-b border-slate-200">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Apply for {job.title}</h1>
            <p className="text-slate-500 mt-1">{job.location || "Kolkata, WB"}</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            {/* --- Success & Error Messages --- */}
            {error && (
              <div className="flex items-start gap-3 bg-red-50 text-red-700 p-4 rounded-lg mb-6 border border-red-200">
                <FiAlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5"/>
                <p>{error}</p>
              </div>
            )}
            {successMessage && (
              <div className="flex items-start gap-3 bg-green-50 text-green-700 p-4 rounded-lg mb-6 border border-green-200">
                <FiCheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5"/>
                <p>{successMessage}</p>
              </div>
            )}

            <div className="space-y-8">
              {/* --- Section: Personal Information --- */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-slate-700 border-b pb-2">Personal Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-600 mb-1">Full Name</label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-600 mb-1">Email Address</label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-600 mb-1">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                  </div>
                   <div>
                    <label htmlFor="dob" className="block text-sm font-medium text-slate-600 mb-1">Date of Birth</label>
                    <input type="date" id="dob" name="dob" value={formData.dob} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                  </div>
                </div>
              </div>

              {/* --- Section: Professional Details --- */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-slate-700 border-b pb-2">Professional Details</h2>
                <div>
                    <label htmlFor="resume" className="block text-sm font-medium text-slate-600 mb-1">Upload Resume</label>
                    <label className="w-full flex items-center gap-4 p-3 bg-slate-50 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                        <FiUpload className="text-slate-500"/>
                        <span className="text-slate-600">
                            {formData.resume ? formData.resume.name : 'Upload file (.pdf, .doc, .docx)'}
                        </span>
                        <input id="resume" name="resume" type="file" onChange={handleChange} accept=".pdf,.doc,.docx" className="sr-only"/>
                    </label>
                </div>
                <div>
                  <label htmlFor="cover_letter" className="block text-sm font-medium text-slate-600 mb-1">Cover Letter (Optional)</label>
                  <textarea id="cover_letter" name="cover_letter" value={formData.cover_letter} onChange={handleChange} rows="4" className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                </div>
                {Array.isArray(job.skills) && job.skills.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">Skills</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {job.skills.map((skill) => (
                        <label key={skill.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input type="checkbox" name="skill" value={skill.name} checked={formData.skill.includes(skill.name)} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                          {skill.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* --- Submit Button --- */}
              <div className="pt-4 border-t border-slate-200">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 disabled:bg-slate-400 transition-all duration-300 transform hover:scale-105"
                >
                  {isSubmitting ? (
                      <>
                        <FiLoader className="animate-spin" /> Submitting...
                      </>
                  ) : "Submit Application"}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </main>
  );
};

export default ApplyPage;